import React, { useState } from 'react';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/solid';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Disable submit button during submission
    const submitButton = e.currentTarget.querySelector(
      'button[type="submit"]'
    ) as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending...';
    }

    try {
      // Call the Cloud Function to handle contact form submission
      const response = await fetch(
        'https://europe-west1-rentsmart-6e31b.cloudfunctions.net/sendContactEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      // Show success message and reset form
      setSuccessMessage(
        'Thank you for your message. We will get back to you soon!'
      );
      setFormData({ name: '', email: '', subject: '', message: '' });

      // Hide the success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert(
        'There was an error submitting your message. Please try again later.'
      );
    } finally {
      // Re-enable submit button
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Send Message';
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-10 sm:py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          <ChatBubbleLeftRightIcon className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-3 sm:mb-4" />
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
            Get in Touch
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 max-w-2xl mx-auto px-4">
            Have a question or feedback? We'd love to hear from you. Our team
            typically responds within 24-48 hours.
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="container mx-auto px-4 max-w-6xl mt-6 sm:mt-8">
          <div className="flex items-center bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 text-green-800 p-4 sm:p-5 md:p-6 rounded-2xl shadow-lg">
            <CheckCircleIcon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mr-2 sm:mr-3 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-base sm:text-lg mb-0.5 sm:mb-1">
                Message Sent Successfully!
              </h3>
              <p className="text-sm sm:text-base text-green-700">
                {successMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8 sm:py-10 md:py-12 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Form - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6 md:p-8 lg:p-10 border border-gray-100">
              <div className="flex items-center mb-4 sm:mb-5 md:mb-6">
                <SparklesIcon className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-blue-600 mr-2 sm:mr-3" />
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                  Send us a Message
                </h2>
              </div>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-7 md:mb-8">
                Fill out the form below and we'll get back to you as soon as
                possible.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help you?"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us more about your inquiry..."
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-base sm:text-lg hover:from-blue-700 hover:to-purple-700 transform hover:scale-[1.02] transition duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <EnvelopeIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Send Message
                </button>
              </form>
            </div>
          </div>

          {/* Sidebar - Contact Info & Features */}
          <div className="space-y-5 sm:space-y-6">
            {/* Contact Information Card */}
            <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-6 border border-gray-100">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <EnvelopeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mr-2" />
                Contact Info
              </h3>
              <div className="space-y-4">
                <a
                  href="mailto:support@rentsmart.ie"
                  className="flex items-start p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition duration-200"
                >
                  <EnvelopeIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-gray-600">
                      Email us
                    </div>
                    <div className="text-blue-600 font-semibold break-all">
                      support@rentsmart.ie
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Response Time Card */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-xl p-5 sm:p-6 border border-purple-100">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mr-2" />
                Response Time
              </h3>
              <div className="space-y-3">
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm">Usually within 24-48 hours</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm">Monday - Friday support</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  <span className="text-sm">Dedicated support team</span>
                </div>
              </div>
            </div>

            {/* Why Contact Us Card */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-2xl shadow-xl p-5 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 flex items-center">
                <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                We're Here to Help
              </h3>
              <ul className="space-y-2.5 sm:space-y-3 text-xs sm:text-sm text-blue-50">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Questions about listings</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Technical support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Account assistance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Partnership inquiries</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
